import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import {
  applyAiProposal,
  approveAiProposal,
  generateLocationDescriptionTask,
  getAiTask,
  getApiErrorMessage,
  listAiProposals,
  type AiProposal,
} from '../lib/api';
import { AI_CREATE_LOCATION_TEMPLATE } from '../templates/aiCreateLocationTemplate';

export default function AiCreateLocationPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => AI_CREATE_LOCATION_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const sourceInput = root.querySelector<HTMLTextAreaElement>('#ai-location-source');
    const generateBtn = root.querySelector<HTMLButtonElement>('#ai-location-generate-btn');
    const applyBtn = root.querySelector<HTMLButtonElement>('#ai-location-apply-btn');
    const discardBtn = root.querySelector<HTMLButtonElement>('#ai-location-discard-btn');
    const statusBadge = root.querySelector<HTMLElement>('#ai-location-status');
    const titleEl = root.querySelector<HTMLElement>('#ai-location-title');
    const slugEl = root.querySelector<HTMLElement>('#ai-location-slug');
    const descriptionEl = root.querySelector<HTMLElement>('#ai-location-description');
    const latEl = root.querySelector<HTMLElement>('#ai-location-latitude');
    const lonEl = root.querySelector<HTMLElement>('#ai-location-longitude');
    const tagsWrap = root.querySelector<HTMLElement>('#ai-location-tags');
    const loadingOverlay = root.querySelector<HTMLElement>('#ai-location-loading');

    if (
      !sourceInput ||
      !generateBtn ||
      !applyBtn ||
      !discardBtn ||
      !statusBadge ||
      !titleEl ||
      !slugEl ||
      !descriptionEl ||
      !latEl ||
      !lonEl ||
      !tagsWrap ||
      !loadingOverlay
    ) {
      return;
    }

    let disposed = false;
    let currentProposal: Record<string, unknown> | null = null;
    let currentProposalId: number | null = null;

    const extractTitle = (text: string): string => {
      const line = text.split('\n').find((item) => item.trim().length > 0)?.trim() ?? '';
      if (!line) return 'AI Generated Location';
      return line.slice(0, 120);
    };

    const extractCoordinates = (text: string): { latitude?: number; longitude?: number } => {
      const match = text.match(/(-?\d{1,2}\.\d+)\s*[, ]\s*(-?\d{1,3}\.\d+)/);
      if (!match) return {};
      const latitude = Number(match[1]);
      const longitude = Number(match[2]);
      if (Number.isNaN(latitude) || Number.isNaN(longitude)) return {};
      return { latitude, longitude };
    };

    const setStatus = (
      label: string,
      kind: 'ready' | 'loading' | 'error' | 'success'
    ) => {
      statusBadge.textContent = label;
      if (kind === 'loading') {
        statusBadge.className =
          'px-2 py-1 bg-amber-100 text-amber-700 text-[10px] font-bold rounded uppercase tracking-widest';
        return;
      }
      if (kind === 'error') {
        statusBadge.className =
          'px-2 py-1 bg-red-100 text-red-700 text-[10px] font-bold rounded uppercase tracking-widest';
        return;
      }
      if (kind === 'success') {
        statusBadge.className =
          'px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded uppercase tracking-widest';
        return;
      }
      statusBadge.className =
        'px-2 py-1 bg-slate-100 text-slate-700 text-[10px] font-bold rounded uppercase tracking-widest';
    };

    const setLoading = (loading: boolean) => {
      loadingOverlay.classList.toggle('hidden', !loading);
      generateBtn.disabled = loading;
      applyBtn.disabled = loading;
      discardBtn.disabled = loading;
      generateBtn.innerHTML = loading
        ? '<span class="material-symbols-outlined text-lg animate-spin">progress_activity</span>Generating...'
        : '<span class="material-symbols-outlined text-lg">auto_awesome</span>Generate Data';
    };

    const renderFromProposal = (proposal: Record<string, unknown>) => {
      const title = typeof proposal.title === 'string' ? proposal.title : 'AI Generated Location';
      const slug = typeof proposal.slug === 'string' ? proposal.slug : '';
      const description = typeof proposal.description === 'string' ? proposal.description : '';
      const latitude = typeof proposal.latitude === 'string' ? proposal.latitude : '';
      const longitude = typeof proposal.longitude === 'string' ? proposal.longitude : '';
      const tags = Array.isArray(proposal.tag_ids) ? proposal.tag_ids : [];

      titleEl.textContent = title;
      slugEl.textContent = slug || 'generated-after-apply';
      descriptionEl.textContent = description || 'No description generated.';
      latEl.textContent = latitude || '-';
      lonEl.textContent = longitude || '-';
      tagsWrap.innerHTML = tags
        .map(
          (tag) =>
            `<span class="px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full border border-primary/20">#${String(tag)}</span>`
        )
        .join('');
      currentProposal = proposal;
    };

    const pollTaskAndLoadProposal = async (taskId: number): Promise<AiProposal | null> => {
      for (let attempt = 0; attempt < 30; attempt += 1) {
        if (disposed) return null;
        const task = await getAiTask(taskId);
        if (task.status === 'completed') {
          const proposalsPayload = await listAiProposals({ task: taskId });
          const first =
            proposalsPayload.results.find((item) => item.proposal_type === 'location_create') ?? proposalsPayload.results[0];
          return first ?? null;
        }
        if (task.status === 'failed') {
          throw new Error(task.error_message || 'AI task failed.');
        }
        await new Promise((resolve) => {
          window.setTimeout(resolve, 1200);
        });
      }
      throw new Error('AI generation timeout. Please try again.');
    };

    const onGenerate = async () => {
      const source = sourceInput.value.trim();
      if (!source) {
        setStatus('Source required', 'error');
        return;
      }

      setLoading(true);
      setStatus('Generating', 'loading');

      try {
        const title = extractTitle(source);
        const coords = extractCoordinates(source);
        const task = await generateLocationDescriptionTask({
          title,
          requirements: source,
          latitude: coords.latitude,
          longitude: coords.longitude,
        });
        const proposal = await pollTaskAndLoadProposal(task.id);
        if (!proposal || typeof proposal.payload !== 'object' || !proposal.payload) {
          throw new Error('AI returned empty proposal.');
        }
        renderFromProposal(proposal.payload);
        currentProposalId = proposal.id;
        setStatus('Ready to review', 'success');
      } catch (error) {
        setStatus('Generation failed', 'error');
        descriptionEl.textContent = getApiErrorMessage(error, 'Failed to generate AI proposal.');
      } finally {
        setLoading(false);
      }
    };

    const onApply = async () => {
      if (!currentProposal) {
        setStatus('Generate first', 'error');
        return;
      }
      if (currentProposalId) {
        setLoading(true);
        try {
          await approveAiProposal(currentProposalId, 'Approved from AI page');
          const applied = await applyAiProposal(currentProposalId);
          const appliedId = applied.target_id;
          if (typeof appliedId === 'number') {
            navigate(`/locations/view?id=${appliedId}`);
            return;
          }
        } catch {
          // fallback to manual apply-to-form flow
        } finally {
          setLoading(false);
        }
      }
      sessionStorage.setItem('ai_location_draft', JSON.stringify(currentProposal));
      navigate('/locations/create');
    };

    const onDiscard = () => {
      currentProposal = null;
      currentProposalId = null;
      sourceInput.value = '';
      titleEl.textContent = '-';
      slugEl.textContent = '-';
      descriptionEl.textContent = 'No data yet. Click "Generate Data" to build a structured draft.';
      latEl.textContent = '-';
      lonEl.textContent = '-';
      tagsWrap.innerHTML = '';
      setStatus('Ready to review', 'ready');
    };

    const onGenerateClick = () => {
      void onGenerate();
    };
    const onApplyClick = () => {
      void onApply();
    };

    generateBtn.addEventListener('click', onGenerateClick);
    applyBtn.addEventListener('click', onApplyClick);
    discardBtn.addEventListener('click', onDiscard);

    onDiscard();

    return () => {
      disposed = true;
      generateBtn.removeEventListener('click', onGenerateClick);
      applyBtn.removeEventListener('click', onApplyClick);
      discardBtn.removeEventListener('click', onDiscard);
    };
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | AI Create Location</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
